/*
 * ═══════════════════════════════════════════════════════════════════════════
 * NEIGHBORHOODS — one source for every surface that names an area.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Three places used to keep their own copy of this list:
 *   · service-areas.tsx  ST_PETE_HOODS  (slug, name, blurb, card photo)
 *   · areas.$slug.tsx    HOOD_DATA      (name, zip, body)
 *   · scripts/generate-seo-assets.mjs   INDEXABLE_PAGES
 *
 * They disagreed, and the disagreement shipped: the generator listed TEN city
 * pages (`/areas/tampa`, `/areas/clearwater`, …) that HOOD_DATA had no entry
 * for, so all ten returned HTTP 200 with the body "Area not found" — soft-404s
 * submitted to Google at priority 0.8. Meanwhile the six neighborhoods that DO
 * exist were in no sitemap at all.
 *
 * Now there is one list. The sitemap generator reads the slugs straight out of
 * this file (see scripts/generate-seo-assets.mjs), so it is structurally
 * impossible to index an area page that does not exist.
 *
 * ── Adding a neighborhood ───────────────────────────────────────────────────
 * Add an entry here. That is the whole job: the card on /service-areas, the
 * detail page at /areas/<slug>, and the sitemap entry all follow.
 *
 * ⚠️ Photos are stock placeholders. Replace with real photos of the actual
 * neighborhood as soon as we have them — a St. Pete buyer recognises their own
 * street, and the alt text is a local-SEO signal. See
 * docs/REDESIGN-WORKING-NOTES.md R21.
 */

export type Area = {
  slug: string;
  name: string;
  zip: string;
  /** Short line for the card on /service-areas. */
  blurb: string;
  /** Full copy for the detail page at /areas/<slug>. */
  body: string;
  photo: string;
  photoAlt: string;
};

export const AREAS: Area[] = [
  {
    slug: 'old-northeast',
    name: 'Old Northeast',
    zip: '33704',
    blurb:
      'Brick streets, jacaranda canopies, Craftsman and Mediterranean Revival. Our oldest relationships.',
    body:
      "The bungalows, the brick streets, the jacarandas in May. Some of the most particular homeowners in Tampa Bay — and some of our longest-running relationships. We understand the quirks of the neighborhood's housing stock: the hundred-year-old hardwood floors that can't handle a wet mop, the stained-glass transoms that need microfiber only, the detached garages that get their own visit on quarterly deep cleans.",
    photo: 'https://images.unsplash.com/photo-1713282951150-4f7787471ea4?w=1600&h=900&fit=crop&q=80',
    photoAlt: 'Craftsman bungalow with a deep front porch on a brick street in Old Northeast, St. Petersburg',
  },
  {
    slug: 'snell-isle',
    name: 'Snell Isle',
    zip: '33704',
    blurb: 'Mediterranean villas, larger footprints, long-standing owners. We bring extra microfiber.',
    body:
      'Mediterranean villas, larger footprints, long-standing owners. Homes that have been in families for decades. We show up with extra microfiber and treat every surface like it matters.',
    photo: 'https://images.unsplash.com/photo-1623705022131-5de1fd1047b2?w=1600&h=900&fit=crop&q=80',
    photoAlt: 'Mediterranean-style waterfront villa with a tile roof in Snell Isle, St. Petersburg',
  },
  {
    slug: 'historic-kenwood',
    name: 'Historic Kenwood',
    zip: '33711',
    blurb: 'Craftsman bungalows, artists, preservation-minded owners. Careful cleaning is the standard here.',
    body:
      'Craftsman bungalows, artists, preservation-minded owners who know what they have. Careful cleaning is the standard here — nothing gets rushed.',
    photo: 'https://images.unsplash.com/photo-1651630535674-198c08635719?w=1600&h=900&fit=crop&q=80',
    photoAlt: 'Restored craftsman bungalow with period detailing in Historic Kenwood, St. Petersburg',
  },
  {
    slug: 'crescent-heights',
    name: 'Crescent Heights',
    zip: '33704',
    blurb: 'Families with young kids — weekly service is the norm in this neighborhood.',
    body:
      'Families with young kids and weekly rhythms. This neighborhood moves fast and so do we. Same crew, same Thursday morning, every time.',
    photo: 'https://images.unsplash.com/photo-1641513253653-a41bc1597ac5?w=1600&h=900&fit=crop&q=80',
    photoAlt: 'Family home with a wide front yard on a quiet residential street in Crescent Heights, St. Petersburg',
  },
  {
    slug: 'shore-acres',
    name: 'Shore Acres',
    zip: '33702',
    blurb: 'Mid-century homes, cul-de-sac streets, young families building roots.',
    body:
      'Mid-century homes, cul-de-sac streets, young families building roots. Homes that are lived in hard and need a crew that can keep up.',
    photo: 'https://images.unsplash.com/photo-1693837851506-93d3d01d4f44?w=1600&h=900&fit=crop&q=80',
    photoAlt: 'Mid-century single-storey home on a cul-de-sac in Shore Acres, St. Petersburg',
  },
  {
    slug: 'bayshore',
    name: 'Bayshore',
    zip: '33701',
    blurb: 'Views from every window, salt air in every seam. We know what that does to a house.',
    body:
      'Views from every window, salt air in every seam. We know what coastal air does to a house. We come prepared.',
    photo: 'https://images.unsplash.com/photo-1719008680894-80974437102f?w=1600&h=900&fit=crop&q=80',
    photoAlt: 'Coastal home with water views and palms in Bayshore, St. Petersburg',
  },
];

/** Lookup by slug, for the detail route. */
export const AREA_BY_SLUG: Record<string, Area> = Object.fromEntries(
  AREAS.map((a) => [a.slug, a]),
);

/**
 * Cities we serve but do not have a dedicated page for.
 *
 * These are named as text on /service-areas ONLY. They deliberately have no
 * /areas/<slug> route and are NOT in the sitemap — ten of them used to be, all
 * of them soft-404ing. Give a city a real page by adding it to AREAS above
 * with real copy; never by adding a URL to the sitemap generator.
 */
export const NEARBY_CITIES = [
  'Tampa', 'Clearwater', 'Largo', 'Dunedin', 'Palm Harbor',
  'Safety Harbor', 'Seminole', 'Bradenton', 'Sarasota',
] as const;
